/**
 * Analysis Progress Component
 * Displays the progress of a running analysis with workflow steps
 */

import { Loader } from 'lucide-react';
import { WorkflowStepList } from './WorkflowStepList';
import { reconcileTerminalSteps } from './reconcileTerminalSteps';
import { DisconnectedBanner } from './DisconnectedBanner';
import { formatDurationMs, formatElapsedTime } from '../../utils/formatters';
import type { JobStatus } from '../../hooks/useAnalysisJob';

interface AnalysisProgressProps {
  jobStatus: JobStatus;
  progressPercent: number;
  elapsedSeconds: number;
  showDisconnectedBanner: boolean;
  cancelling: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onNavigateToRepositories: () => void;
  onViewResults?: () => void;
  onNavigateToOAuth?: () => void;
  onNavigateToBilling?: () => void;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  jobStatus,
  progressPercent,
  elapsedSeconds,
  showDisconnectedBanner,
  cancelling,
  onCancel,
  onRetry,
  onNavigateToRepositories,
  onViewResults,
  onNavigateToOAuth,
  onNavigateToBilling,
}) => {
  const isFailed = jobStatus.status === 'failed';
  const isCompleted = jobStatus.status === 'completed';
  const isQueued = jobStatus.status === 'queued';
  const isRunning =
    jobStatus.status === 'running' ||
    jobStatus.status === 'pending' ||
    jobStatus.status === 'queued';

  return (
    <>
      {/* Connection Status Warning */}
      {isRunning && showDisconnectedBanner && <DisconnectedBanner />}

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xs rounded-lg p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
              isFailed
                ? 'bg-red-100 dark:bg-red-900/30'
                : isCompleted
                  ? 'bg-teal-100 dark:bg-teal-900/30'
                  : 'bg-teal-50 dark:bg-teal-900/20'
            }`}
          >
            {isFailed ? (
              <svg
                className="w-10 h-10 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : isCompleted ? (
              <svg
                className="w-10 h-10 text-teal-600 dark:text-teal-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <Loader className="w-10 h-10 text-teal-600 dark:text-teal-400 animate-spin" />
            )}
          </div>
          <h1
            className={`text-2xl font-bold mb-2 ${
              isFailed ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
            }`}
          >
            {isFailed
              ? jobStatus.errorInfo?.title || 'Analysis Failed'
              : isCompleted
                ? 'Analysis Complete'
                : isQueued
                  ? 'Analysis Queued'
                  : 'Analyzing Your Code'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isFailed
              ? jobStatus.errorInfo?.message || 'The analysis encountered an error.'
              : isCompleted
                ? 'Your analysis has finished. View the results to see insights and recommendations.'
                : isQueued
                  ? 'Your analysis is in the queue and will start as soon as a worker is available.'
                  : "We're examining your code to provide insights and recommendations"}
          </p>
          {isFailed && jobStatus.errorInfo?.action && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {jobStatus.errorInfo.action}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center mb-6">
          {isFailed ? (
            <div className="flex gap-3">
              {jobStatus.errorInfo?.actionType === 'reconnect' ? (
                <button
                  onClick={() => onNavigateToOAuth?.()}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium shadow-xs"
                >
                  Reconnect Account
                </button>
              ) : jobStatus.errorInfo?.actionType === 'upgrade' ? (
                <button
                  onClick={() => onNavigateToBilling?.()}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium shadow-xs"
                >
                  View Plans
                </button>
              ) : jobStatus.errorInfo?.canRetry !== false ? (
                <button
                  onClick={onRetry}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium shadow-xs"
                >
                  Try Again
                </button>
              ) : null}
              <button
                onClick={onNavigateToRepositories}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium shadow-xs"
              >
                Back to Repositories
              </button>
            </div>
          ) : isCompleted ? (
            <div className="flex gap-3">
              {onViewResults && (
                <button
                  onClick={onViewResults}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium shadow-xs"
                >
                  View Results
                </button>
              )}
              <button
                onClick={onNavigateToRepositories}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium shadow-xs"
              >
                Back to Repositories
              </button>
            </div>
          ) : (
            <button
              onClick={onCancel}
              disabled={cancelling}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium shadow-xs"
            >
              {cancelling ? 'Cancelling...' : 'Cancel Analysis'}
            </button>
          )}
        </div>

        {/* Overall Progress Bar — hidden when queued or failed with no progress */}
        {!isQueued && !(isFailed && progressPercent === 0) && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {isCompleted ? 'Completed' : jobStatus.progress?.step || 'Overall Progress'}
              </span>
              <div className="flex items-center gap-2">
                {isRunning && !isQueued && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Elapsed: {formatElapsedTime(elapsedSeconds)}
                  </span>
                )}
                {(isFailed || isCompleted) &&
                  jobStatus.workflow &&
                  jobStatus.workflow.totalTimeSeconds > 0 && (
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      Total: {formatDurationMs(jobStatus.workflow.totalTimeMs)}
                    </span>
                  )}
                <span className="px-3 py-1 text-sm font-bold rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700">
                  {isCompleted ? 100 : progressPercent}%
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden relative">
              <div
                className="bg-teal-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${isCompleted ? 100 : progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Completed Steps Counter */}
        {!isQueued &&
          jobStatus.workflow &&
          jobStatus.workflow.steps &&
          jobStatus.workflow.totalSteps > 0 && (
            <div className="text-center text-xs text-gray-600 dark:text-gray-400 mb-4">
              {jobStatus.workflow.completedSteps} of {jobStatus.workflow.totalSteps} steps completed
            </div>
          )}

        {/* Simple Current Step Display (when no workflow data) */}
        {!jobStatus.workflow?.steps && jobStatus.progress?.step && (
          <div className="mb-6">
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-800/30">
              <div className="flex items-center">
                <Loader className="w-5 h-5 text-teal-500 animate-spin mr-3 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">
                    {jobStatus.progress.step}
                  </p>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-0.5">In progress...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Steps */}
        {jobStatus.workflow && jobStatus.workflow.steps && (
          <WorkflowStepList
            steps={reconcileTerminalSteps(
              jobStatus.workflow.steps,
              jobStatus.status,
              jobStatus.errorInfo?.message ?? jobStatus.error,
            )}
          />
        )}
      </div>
    </>
  );
};
